import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent
} from "@mui/material";
import {
  McTable,
  McButton,
  McTag,
  McPagination,
  McModal
} from "@maersk-global/mds-react-wrapper";
import { McTabBar } from "@maersk-global/mds-react-wrapper/components-core/mc-tab-bar";
import { McTab } from "@maersk-global/mds-react-wrapper/components-core/mc-tab";
import OnboardingRequestDetailsDrawer from "./OnboardingRequestDetailsDrawer";
import axios from "axios";
import { toast } from 'react-toastify';

const OnboardingRequestsTable = ({ 
  userRequests = [], 
  pendingApprovals = [], 
  title = "My Onboarding Requests",
  onPendingApprovalsRefresh,
  onUserRequestsRefresh,
  showOnlyPendingApprovals = false,
  isChangeManagerDashboard = false
}) => {
  const [selectedRequestTab, setSelectedRequestTab] = useState(showOnlyPendingApprovals ? 1 : 0);

  // Define the Maersk font family
  const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25; // Number of records per page

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRequestData, setSelectedRequestData] = useState(null);

  // Delete functionality state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);

  const [selectedRequests, setSelectedRequests] = useState([]);

  // Get current data based on tab selection
  const getCurrentData = () => {
    if (showOnlyPendingApprovals) return pendingApprovals;
    return selectedRequestTab === 0 ? userRequests : pendingApprovals;
  };

  const currentData = getCurrentData();

  // Client-side pagination - calculate total pages and slice data
  const totalPages = Math.ceil(currentData.length / recordsPerPage);
  const paginatedData = currentData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  // Define table columns for user requests
  const userRequestsColumns = [
    {
      id: "applicationId",
      label: "Application ID",
    },
    {
      id: "applicationInstances",
      label: "Application Instances",
    },
    {
      id: "changeProcessType",
      label: "Change Process Type",
    },
    {
      id: "requestStatus",
      label: "Request Status",
    },
    {
      id: "requestedBy",
      label: "Requested By",
    },
    {
      id: "requestedOn",
      label: "Requested On",
    },
    {
      id: "engineeringApproval",
      label: "Engineering Approval",
    },
    {
      id: "changeManagerApproval",
      label: "Change Manager Approval",
    },
    {
      id: "actions",
      label: "Actions",
      sortDisabled: true,
    }
  ];

  // Define table columns for pending approvals (legacy view)
  const pendingApprovalsColumns = [
    {
      id: "requestId",
      label: "Request ID",
    },
    {
      id: "requestStatus",
      label: "Request Status",
    },
    {
      id: "requestedOn",
      label: "Created Date",
    },
    {
      id: "applicationId",
      label: "Application ID",
    },
    {
      id: "applicationInstances",
      label: "Application Instances",
    },
    {
      id: "ownerNames",
      label: "Owner Names",
    },
    {
      id: "changeProcessType",
      label: "Change Process Type",
    },
    {
      id: "requestedBy",
      label: "Requested By",
    },
    {
      id: "engineeringApproval",
      label: "Engineering Approval",
    },
    {
      id: "changeManagerApproval",
      label: "Change Manager Approval",
    },
    {
      id: "approvalNotes",
      label: "Approval Notes",
    },
    {
      id: "actions",
      label: "Actions",
      sortDisabled: true,
    }
  ];

  // Define table columns for pending approvals (Control Tower payload)
  const controlTowerPendingApprovalsColumns = [
    {
      id: "request_id",
      label: "Request ID",
    },
    {
      id: "request_status",
      label: "Request Status",
    },
    {
      id: "created_date",
      label: "Created Date",
    },
    {
      id: "engineering_team_approval_status",
      label: "Engineering Approval",
    },
    {
      id: "change_manager_approval_status",
      label: "Change Manager Approval",
    },
    {
      id: "approval_type",
      label: "Approval Type",
    },
    {
      id: "approval_notes",
      label: "Approval Notes",
    },
    {
      id: "application_id",
      label: "Application ID",
    },
    {
      id: "application_instances",
      label: "Application Instances",
    },
    {
      id: "owner_names",
      label: "Owner Names",
    },
    {
      id: "change_process_type",
      label: "Change Process Type",
    },
    {
      id: "requested_by",
      label: "Requested By",
    },
    {
      id: "actions",
      label: "Actions",
      sortDisabled: true,
    }
  ];

  // Define table columns for Change Manager Dashboard
  const changeManagerDashboardColumns = [
    {
      id: "application_id",
      label: "Application ID",
    },
    {
      id: "application_instances",
      label: "Application Instances",
    },
    {
      id: "change_process_type",
      label: "Change Process Type",
    },
    {
      id: "owner_names",
      label: "Owner Names",
    },
    {
      id: "engineering_team_approval_status",
      label: "Engineering Approval",
    },
    {
      id: "change_manager_approval_status",
      label: "Change Manager Approval",
    },
    {
      id: "created_date",
      label: "Requested On",
    },
    {
      id: "requested_by",
      label: "Requested By",
    },
    {
      id: "actions",
      label: "Actions",
      sortDisabled: true,
    }
  ];

  // Get current columns based on tab
  const getCurrentColumns = () => {
    if (showOnlyPendingApprovals) {
      if (isChangeManagerDashboard) return changeManagerDashboardColumns;
      return controlTowerPendingApprovalsColumns;
    }
    return selectedRequestTab === 0 ? userRequestsColumns : pendingApprovalsColumns;
  };

  useEffect(() => {
    if (showOnlyPendingApprovals) {
      setSelectedRequestTab(1);
    }
  }, [showOnlyPendingApprovals]);

  // Transform data for McTable
  const transformedData = useMemo(() => {
    return paginatedData.map((item, index) => {
      if (selectedRequestTab === 0) {
        // User requests data structure
        return {
          id: `request-${index}`,
          applicationId: item.onboarding_details?.application_id || 'N/A',
          applicationInstances: item.onboarding_details?.application_instances || [],
          changeProcessType: item.onboarding_details?.change_process_type || 'N/A',
          requestStatus: item.request_status || 'N/A',
          requestedBy: item.onboarding_details?.requested_by || 'N/A',
          requestedOn: item.created_date || 'N/A',
          engineeringApproval: item.onboarding_details?.engineering_team_approval_status || 'N/A',
          changeManagerApproval: item.onboarding_details?.change_manager_approval_status || 'N/A',
          _originalData: item
        };
      } else {
        // Pending approvals data structure
        return {
          id: `approval-${index}`,
          requestId: item.request_id || 'N/A',
          requestStatus: item.request_status || 'N/A',
          requestedOn: item.created_date || 'N/A',
          applicationId: item.onboarding_details?.application_id || 'N/A',
          applicationInstances: item.onboarding_details?.application_instances || [],
          ownerNames: item.onboarding_details?.owner_names || [],
          changeProcessType: item.onboarding_details?.change_process_type || 'N/A',
          requestedBy: item.onboarding_details?.requested_by || 'N/A',
          engineeringApproval: item.engineering_team_approval_status || 'N/A',
          changeManagerApproval: item.change_manager_approval_status || 'N/A',
          approvalNotes: item.approval_notes || 'N/A',
          request_id: item.request_id || 'N/A',
          request_status: item.request_status || 'N/A',
          created_date: item.created_date || 'N/A',
          engineering_team_approval_status: item.engineering_team_approval_status || 'N/A',
          change_manager_approval_status: item.change_manager_approval_status || 'N/A',
          approval_type: item.approval_type || 'N/A',
          approval_notes: item.approval_notes || 'N/A',
          application_id: item.onboarding_details?.application_id || 'N/A',
          application_instances: item.onboarding_details?.application_instances || [],
          owner_names: item.onboarding_details?.owner_names || [],
          change_process_type: item.onboarding_details?.change_process_type || 'N/A',
          requested_by: item.onboarding_details?.requested_by || 'N/A',
          _originalData: item
        };
      }
    });
  }, [paginatedData, selectedRequestTab]);

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to get status appearance
  const getStatusAppearance = (status) => {
    switch (status?.toLowerCase()) {
      case 'processing':
      case 'pending':
        return 'warning';
      case 'approved':
      case 'completed':
        return 'success';
      case 'rejected':
      case 'failed':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Helper function to get approval status display
  const getApprovalStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status || 'N/A';
    }
  };

  // Helper function to get process type display
  const getProcessTypeDisplay = (changeType) => {
    switch (changeType?.toLowerCase()) {
      case 'classic':
        return 'Manual';
      case 'rapid':
        return 'Automated';
      default:
        return changeType || 'N/A';
    }
  };

  // Helper function to format approval type
  const formatApprovalType = (approvalType) => {
    return approvalType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';
  };

  // Handle pagination change
  const handlePageChange = (event) => {
    setCurrentPage(event.detail);
  };

  // Handle view button click
  const handleViewClick = (item) => {
    setSelectedRequestData(item);
    setIsDrawerOpen(true);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedRequestData(null);
  };

  // Handle row selection
  const handleSelectChange = (e) => {
    const newSelection = (e.detail || []);
    setSelectedRequests(newSelection);
  };

  // Handle delete button click
  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteSubmit = async () => {
    setLoadingAction("delete");
    
    try {
      // selectedRequests contains the actual row objects from the selection event
      const requestIds = selectedRequests.map(row => row._originalData?.request_id).filter(Boolean);

      if (requestIds.length === 0) {
        console.error('No valid request IDs found');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/delete_onboarding_requests`,
        { request_ids: requestIds },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('onboardingToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // Reset selections and close modal
        setSelectedRequests([]);
        setIsDeleteModalOpen(false);
        toast.success("Successfully deleted onboarding request(s)", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light"
        });
        
        // Refresh the user requests data
        if (onUserRequestsRefresh) {
          onUserRequestsRefresh();
        }
      }
    } catch (error) {
      console.error('Error deleting requests:', error);
    } finally {
      setLoadingAction(null);
    }
  };

  // Reset to first page when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedRequestTab, currentData.length]);

  // Reset selected rows when changing tabs
  React.useEffect(() => {
    setSelectedRequests([]);
  }, [selectedRequestTab]);

  return (
    <>
      <Card sx={{ 
        mb: 3, 
        mt: 2,
        border: '1px solid #d9d9d9', 
        boxShadow: 'none',
        borderRadius: '10px'
      }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" component="div" gutterBottom sx={{ fontFamily: maerskFontFamily }}>
              {title}
            </Typography>
            
            {/* Tab Bar for Request Types */}
            {!showOnlyPendingApprovals && (
              <Box sx={{ mb: 3 }}>
                <McTabBar 
                  tabchange={(e) => setSelectedRequestTab(e.detail)} 
                  currentindex={selectedRequestTab}
                >
                  <McTab 
                    slot="tab" 
                    label="My Requests" 
                    active={selectedRequestTab === 0}
                  >
                    <span slot="suffix">
                      <mc-tag 
                        appearance="info" 
                        fit="small" 
                        label={userRequests.length.toString()}
                      ></mc-tag>
                    </span>
                  </McTab>
                  <McTab 
                    slot="tab" 
                    label="Pending Approvals" 
                    active={selectedRequestTab === 1}
                  >
                    <span slot="suffix">
                      <mc-tag 
                        appearance="info" 
                        fit="small" 
                        label={pendingApprovals.length.toString()}
                      ></mc-tag>
                    </span>
                  </McTab>
                </McTabBar>
              </Box>
            )}

            {/* Delete Button for My Requests Tab */}
            {!showOnlyPendingApprovals && selectedRequestTab === 0 && selectedRequests.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#E9F6FC",
                    borderRadius: "8px",
                    padding: "12px",
                    gap: "8px",
                    width: "fit-content"
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                    {selectedRequests.length} selected
                  </Typography>
                  <McButton 
                    label="Delete" 
                    fit="small" 
                    appearance="neutral"
                    click={handleDelete}
                  />
                </Box>
              </Box>
            )}

            <McTable
              data={transformedData}
              columns={getCurrentColumns()}
              datakey="id"
              headersticky
              fit="small"
              select={selectedRequestTab === 0} // Enable selection only for My Requests tab
              selected={selectedRequests}
              selectchange={handleSelectChange}
              customstyles={`
                .mds-table {
                  max-height: 70vh !important;
                }
              `}
              sortdefaultcolumnid={
                selectedRequestTab === 1
                  ? showOnlyPendingApprovals
                    ? "created_date"
                    : "requestedOn"
                  : "requestedOn"
              }
              sortdefaultdirection="descending"
            >
              {transformedData?.length === 0 && (
                <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.2rem" }}>
                  {selectedRequestTab === 0 ? "No Onboarding Requests Found" : "No Pending Approvals Found"}
                </div>
              )}
              
              {transformedData?.map((row) => (
                <React.Fragment key={row.id}>

                  {/* Application ID Cell */}
                  <div slot={`${row.id}_applicationId`}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                      {row.applicationId}
                    </Typography>
                  </div>

                  {/* Application Instances Cell */}
                  <div slot={`${row.id}_applicationInstances`}>
                    <Box>
                      {Array.isArray(row.applicationInstances) && row.applicationInstances.length > 0 ? (
                        row.applicationInstances.map((instance, index) => (
                          <Typography 
                            key={index} 
                            variant="body2" 
                            sx={{ 
                              fontFamily: maerskFontFamily,
                              display: 'block',
                              mb: index < row.applicationInstances.length - 1 ? 0.5 : 0
                            }}
                          >
                            {instance}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontStyle: 'italic', color: 'text.secondary' }}>
                          No instances
                        </Typography>
                      )}
                    </Box>
                  </div>

                  {/* Change Process Type Cell */}
                  <div slot={`${row.id}_changeProcessType`}>
                    <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                      {getProcessTypeDisplay(row.changeProcessType)}
                    </Typography>
                  </div>

                  {/* Request ID Cell (only for pending approvals and not change manager dashboard) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && !isChangeManagerDashboard && (
                    <div slot={`${row.id}_request_id`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 600 }}>
                        {row.request_id}
                      </Typography>
                    </div>
                  )}

                  {/* Request Status Cell (only for pending approvals and not change manager dashboard) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && !isChangeManagerDashboard && (
                    <div slot={`${row.id}_request_status`}>
                      <McTag
                        label={row.request_status}
                        appearance={getStatusAppearance(row.request_status)}
                        size="small"
                      />
                    </div>
                  )}

                  {/* Created Date Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_created_date`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {formatDate(row.created_date)}
                      </Typography>
                    </div>
                  )}

                  {/* Owner Names Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_owner_names`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {Array.isArray(row.owner_names) && row.owner_names.length > 0
                          ? row.owner_names.join(", ")
                          : "N/A"}
                      </Typography>
                    </div>
                  )}

                  {/* Engineering Approval Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_engineering_team_approval_status`}>
                      <McTag
                        label={getApprovalStatusDisplay(row.engineering_team_approval_status)}
                        appearance={getStatusAppearance(row.engineering_team_approval_status)}
                        size="small"
                      />
                    </div>
                  )}

                  {/* Change Manager Approval Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_change_manager_approval_status`}>
                      <McTag
                        label={getApprovalStatusDisplay(row.change_manager_approval_status)}
                        appearance={getStatusAppearance(row.change_manager_approval_status)}
                        size="small"
                      />
                    </div>
                  )}

                  {/* Approval Type Cell (only for pending approvals and not change manager dashboard) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && !isChangeManagerDashboard && (
                    <div slot={`${row.id}_approval_type`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {formatApprovalType(row.approval_type)}
                      </Typography>
                    </div>
                  )}

                  {/* Approval Notes Cell (only for pending approvals and not change manager dashboard) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && !isChangeManagerDashboard && (
                    <div slot={`${row.id}_approval_notes`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {row.approval_notes || 'N/A'}
                      </Typography>
                    </div>
                  )}

                  {/* Application ID Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_application_id`}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                        {row.application_id}
                      </Typography>
                    </div>
                  )}

                  {/* Application Instances Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_application_instances`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {Array.isArray(row.application_instances) && row.application_instances.length > 0
                          ? row.application_instances.join(", ")
                          : "N/A"}
                      </Typography>
                    </div>
                  )}

                  {/* Change Process Type Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_change_process_type`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {getProcessTypeDisplay(row.change_process_type)}
                      </Typography>
                    </div>
                  )}

                  {/* Requested By Cell (only for pending approvals) */}
                  {selectedRequestTab === 1 && showOnlyPendingApprovals && (
                    <div slot={`${row.id}_requested_by`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {row.requested_by}
                      </Typography>
                    </div>
                  )}

                  {/* Legacy Request ID Cell (pending approvals in original view) */}
                  {selectedRequestTab === 1 && !showOnlyPendingApprovals && (
                    <div slot={`${row.id}_requestId`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: 600 }}>
                        {row.requestId}
                      </Typography>
                    </div>
                  )}

                  {/* Legacy Owner Names Cell (pending approvals in original view) */}
                  {selectedRequestTab === 1 && !showOnlyPendingApprovals && (
                    <div slot={`${row.id}_ownerNames`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {Array.isArray(row.ownerNames) && row.ownerNames.length > 0
                          ? row.ownerNames.join(", ")
                          : "N/A"}
                      </Typography>
                    </div>
                  )}

                  {/* Legacy Approval Notes Cell (pending approvals in original view) */}
                  {selectedRequestTab === 1 && !showOnlyPendingApprovals && (
                    <div slot={`${row.id}_approvalNotes`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {row.approvalNotes}
                      </Typography>
                    </div>
                  )}

                  {/* Request Status Cell */}
                  {(selectedRequestTab === 0 || !showOnlyPendingApprovals) && (
                    <div slot={`${row.id}_requestStatus`}>
                      <McTag 
                        label={row.requestStatus} 
                        appearance={getStatusAppearance(row.requestStatus)}
                        size="small"
                      />
                    </div>
                  )}

                  {/* Requested By Cell */}
                  {(selectedRequestTab === 0 || !showOnlyPendingApprovals) && (
                    <div slot={`${row.id}_requestedBy`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>{row.requestedBy}</Typography>
                    </div>
                  )}

                  {/* Requested On Cell */}
                  {(selectedRequestTab === 0 || !showOnlyPendingApprovals) && (
                    <div slot={`${row.id}_requestedOn`}>
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        {formatDate(row.requestedOn)}
                      </Typography>
                    </div>
                  )}

                  {/* Engineering Approval Cell (only for user requests) */}
                  {selectedRequestTab === 0 && (
                    <div slot={`${row.id}_engineeringApproval`}>
                      <McTag 
                        label={getApprovalStatusDisplay(row.engineeringApproval)} 
                        appearance={getStatusAppearance(row.engineeringApproval)}
                        size="small"
                      />
                    </div>
                  )}

                  {/* Change Manager Approval Cell (only for user requests) */}
                  {selectedRequestTab === 0 && (
                    <div slot={`${row.id}_changeManagerApproval`}>
                      <McTag 
                        label={getApprovalStatusDisplay(row.changeManagerApproval)} 
                        appearance={getStatusAppearance(row.changeManagerApproval)}
                        size="small"
                      />
                    </div>
                  )}

                  {/* Actions Cell */}
                  <div slot={`${row.id}_actions`}>
                    <McButton 
                      variant="outlined"
                      size="small"
                      appearance="neutral"
                      icon="eye"
                      onClick={() => handleViewClick(row._originalData)}
                    >
                      View
                    </McButton>
                  </div>
                </React.Fragment>
              ))}
            </McTable>

            {/* Pagination Component */}
            {currentData.length > recordsPerPage && (
              <Box sx={{ paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                <McPagination
                  currentpage={currentPage}
                  totalpages={totalPages}
                  pagechange={handlePageChange}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Onboarding Request Details Drawer */}
      <OnboardingRequestDetailsDrawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        requestData={selectedRequestData}
        requestType={selectedRequestTab === 0 ? 'user_request' : 'pending_approval'}
        onApprovalComplete={() => {
          // Only refresh pending approvals if we're on the pending approvals tab
          if (selectedRequestTab === 1 && onPendingApprovalsRefresh) {
            onPendingApprovalsRefresh();
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      <McModal
        zindex={99999}
        body=""
        fit="small"
        open={isDeleteModalOpen}
        closed={() => setIsDeleteModalOpen(false)}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
            height: "100%"
          }}
        >
          <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, textAlign: "center" }}>
            Do you want to delete selected requests?
          </Typography>
        </div>

        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          loading={loadingAction === "delete"}
          click={handleDeleteSubmit}
        >
          Yes
        </McButton>
        <McButton 
          slot="secondaryAction" 
          appearance="neutral" 
          variant="outlined" 
          dialogaction="cancel" 
          fit="medium"
        >
          Cancel
        </McButton>
      </McModal>
    </>
  );
};

export default OnboardingRequestsTable;
